import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileDown, Loader2, ShieldCheck, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, previewSrc, formatApiError, CATEGORY_LABELS } from "../api";
import { Reveal } from "../components/Reveal";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [config, setConfig] = useState(null);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // {code, label, final_cents}
  const [promoError, setPromoError] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, [slug]);

  if (notFound)
    return (
      <div className="mx-auto max-w-3xl px-6 py-32 text-center" data-testid="product-not-found">
        <h1 className="font-serif text-4xl text-ink">This piece has sold through</h1>
        <Link to="/shop" className="mt-6 inline-block text-sage underline underline-offset-4">Back to the shop</Link>
      </div>
    );
  if (!product)
    return <div className="flex min-h-[50vh] items-center justify-center" data-testid="product-loading"><Loader2 className="animate-spin text-sage" /></div>;

  const previews = product.previews || [];
  const purchasable = product.purchasable && config?.payments_enabled;

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoBusy(true);
    setPromoError("");
    try {
      const { data } = await api.post("/promos/validate", { code: promoInput.trim(), product_id: product.id });
      if (data.ok) setPromo(data);
      else setPromoError(data.message);
    } catch {
      setPromoError("Couldn't check that code — try again.");
    } finally {
      setPromoBusy(false);
    }
  };

  const buyNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        product_id: product.id,
        origin_url: window.location.origin,
        promo_code: promo?.code || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e, "Checkout could not be started."));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 md:px-8 lg:py-16" data-testid="product-detail-page">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-ink/55 transition-colors hover:text-sage" data-testid="back-to-shop">
        <ArrowLeft size={15} /> Back to shop
      </Link>
      <div className="mt-8 grid gap-12 lg:grid-cols-2 lg:gap-16">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <motion.div
            key={active}
            initial={{ opacity: 0.4, scale: 0.99 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden rounded-[1.8rem] border border-sage/12 bg-white shadow-[0_20px_50px_-20px_rgba(59,87,75,0.2)]"
          >
            {previews[active] ? (
              <img src={previewSrc(previews[active])} alt={product.name} className="aspect-[4/5] w-full object-cover" data-testid="product-main-image" />
            ) : (
              <div className="flex aspect-[4/5] items-center justify-center bg-lavender-tint font-serif text-xl italic text-lavender-deep">
                Preview coming soon
              </div>
            )}
          </motion.div>
          {previews.length > 1 && (
            <div className="mt-4 flex gap-3">
              {previews.map((p, i) => (
                <button
                  key={i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setActive(i)}
                  className={`overflow-hidden rounded-xl border-2 transition-all duration-300 ${
                    i === active ? "border-sage" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={previewSrc(p)} alt={`${product.name} preview ${i + 1}`} className="h-20 w-16 object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <Reveal>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-lavender-deep">
              {CATEGORY_LABELS[product.category] || product.category}
            </p>
            <h1 className="mt-3 font-serif text-4xl font-light leading-tight tracking-tight text-ink sm:text-5xl" data-testid="product-detail-title">
              {product.name}
            </h1>
            <div className="mt-5 flex items-center gap-4" data-testid="product-detail-price">
              <p className="font-mono text-2xl text-sage-deep">
                {fmt(product.price_cents, product.currency)}
              </p>
              {product.compare_at_price_cents > product.price_cents && (
                <>
                  <p className="font-mono text-base text-ink/40 line-through">
                    {fmt(product.compare_at_price_cents, product.currency)}
                  </p>
                  <span className="rounded-full bg-lavender-deep px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-cream" data-testid="product-detail-sale-badge">
                    Sale
                  </span>
                </>
              )}
            </div>
            {product.file_count > 1 && (
              <p className="mt-2 text-xs text-lavender-deep" data-testid="product-bundle-note">
                Bundle — includes {product.file_count} downloadable files
              </p>
            )}
            <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-ink/70" data-testid="product-detail-description">
              {product.description}
            </p>

            <div className="mt-8 space-y-4">
              <div className="rounded-2xl border border-sage/12 bg-white/70 p-6">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-sage">
                  <FileDown size={14} /> Instant download · file formats
                </p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="product-formats">
                  {(product.formats || []).map((f) => (
                    <span key={f} className="rounded-full bg-sage-tint px-4 py-1.5 text-xs text-sage-deep">{f}</span>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-lavender/20 bg-lavender-tint/60 p-6" data-testid="product-license">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-lavender-deep">
                  <BadgeCheck size={14} /> Licence
                </p>
                <p className="mt-3 text-sm leading-relaxed text-ink/70">{product.license_summary}</p>
              </div>
            </div>

            <div className="mt-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/45">Have a promo code?</p>
              <div className="mt-2 flex gap-2">
                <input
                  data-testid="promo-code-input"
                  value={promoInput}
                  onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromo(null); setPromoError(""); }}
                  placeholder="e.g. LAUNCH20"
                  className="w-44 rounded-full border border-sage/20 bg-white/80 px-4 py-2.5 font-mono text-sm uppercase tracking-wider text-ink outline-none transition-colors placeholder:normal-case placeholder:tracking-normal placeholder:text-ink/35 focus:border-sage"
                />
                <button
                  data-testid="promo-apply-button"
                  onClick={applyPromo}
                  disabled={promoBusy || !promoInput.trim()}
                  className="rounded-full border border-sage/30 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-sage transition-all hover:bg-sage hover:text-cream disabled:opacity-50"
                >
                  {promoBusy ? "…" : "Apply"}
                </button>
              </div>
              {promo && (
                <p className="mt-2 flex items-center gap-2 text-sm text-sage-deep" data-testid="promo-applied-message">
                  <BadgeCheck size={15} />
                  {promo.code} applied — {promo.label} · you'll pay {fmt(promo.final_cents, product.currency)}
                </p>
              )}
              {promoError && (
                <p className="mt-2 text-sm text-red-600" data-testid="promo-error-message">{promoError}</p>
              )}
            </div>

            <div className="mt-8">
              {purchasable ? (
                <>
                  <button
                    data-testid="product-buy-now-btn"
                    onClick={buyNow}
                    disabled={busy}
                    className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-sage px-8 py-4.5 py-4 text-sm font-medium tracking-wide text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-sage-deep hover:shadow-[0_18px_35px_-12px_rgba(59,87,75,0.5)] disabled:opacity-60 sm:w-auto"
                  >
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                    {busy ? "Preparing secure checkout…" : "Buy Now — Instant Download"}
                  </button>
                  {config?.stripe_mode === "test" && (
                    <p className="mt-3 text-xs text-ink/45" data-testid="test-mode-note">
                      Checkout is in test mode — no real charge is made. Use card 4242 4242 4242 4242.
                    </p>
                  )}
                </>
              ) : (
                <div className="rounded-2xl border border-ink/10 bg-cream-dark/60 p-6" data-testid="product-unavailable-notice">
                  <p className="font-serif text-lg text-ink">Not available for purchase yet</p>
                  <p className="mt-1 text-sm text-ink/55">
                    This piece is being prepared for sale. Please check back soon.
                  </p>
                </div>
              )}
              <p className="mt-6 max-w-md text-xs leading-relaxed text-ink/45">
                Secure checkout by Stripe with international cards, Apple Pay and Google Pay. Your
                download link is delivered immediately after payment is confirmed, and a receipt is
                emailed to you.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
